import * as React from 'react';
import { useDebounce } from 'use-debounce';
import clsx from 'clsx';

import { config } from '@/forms/config';
import { useNativeInput } from '@/forms/hooks';
import { resolveTypeaheadBlurAction } from '@/forms/utils';

import { Icon } from '../icon/icon.tsx';
import { Hint, type HintContent } from '../hint/hint.tsx';
import { ChipGroup } from '../chip-group/chip-group.tsx';
import { useClickAway } from '../hooks.tsx';

import './dropdown.css';

export interface DropdownDatalistOption {
  value: string;
  key: string;
  icon?: React.ReactNode;
  selected: boolean;
  disabled: boolean;
}

/** How the field treats its text: search the list, keep the text as typed, or refuse it. */
export type DropdownInputMode = 'search' | 'free' | 'blocked';

interface DropdownProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onBlur'
  > {
  label: string;
  leadingIcon?: React.ReactNode;
  /** Content behind the "?" beside the label; without it there is no "?". */
  hint?: HintContent;
  required?: boolean;
  state?: 'default' | 'error' | 'warning';
  stateText?: string;
  onToggle: React.MouseEventHandler<HTMLButtonElement>;
  onRemove?: (option: string) => void;
  stateDefaultIcon?: React.ReactNode;
  variant: 'simple' | 'multi';
  as: 'select' | 'search';
  canAddCustom?: boolean;
  datalist: {
    defaultIcon?: React.ReactNode;
    iconPosition?: 'leading' | 'trailing';
    options: Array<DropdownDatalistOption>;
  };
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Called after a blur reverts unpicked text, with the mode in force at the revert. */
  onInvalidEdit?: (mode: DropdownInputMode) => void;
  /** Maps text to a mode for a simple search field; without it the field always searches. */
  getInputMode?: (text: string) => DropdownInputMode;
  /** Reports each mode change; `live` is true when edited text, not the value, set it. */
  onInputModeChange?: (mode: DropdownInputMode, live: boolean) => void;
  onFocus?: () => void;
  value?: string;
  includeClearIcon?: boolean;
}

const Dropdown = React.forwardRef<HTMLInputElement, DropdownProps>(
  (
    {
      type = 'text',
      disabled,
      onChange,
      onBlur,
      onFocus: onFocusProp,
      onInvalidEdit,
      getInputMode,
      onInputModeChange,
      onKeyDown,
      name,
      hint,
      state = 'default',
      variant,
      onToggle,
      onRemove,
      canAddCustom = false,
      leadingIcon,
      datalist,
      as,
      required,
      stateText,
      stateDefaultIcon,
      label,
      value,
      includeClearIcon = true,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => ref.current as HTMLInputElement,
    );
    const hiddenInputRef = React.useRef<HTMLInputElement>(null);
    const [clicked, setClicked] = React.useState(false);
    const [showNoResultsOption, setShowNoResultsOption] = React.useState(false);
    const [showClearIcon, setShowClearIcon] = React.useState(false);
    const [internalList, setInternalList] = React.useState<
      DropdownDatalistOption[]
    >([]);
    const [internalValue, setInternalValue] = React.useState('');
    // Edited since the value last changed: the typed text, not the value, sets the mode.
    const [edited, setEdited] = React.useState(false);
    const [modeAnnouncement, setModeAnnouncement] = React.useState('');

    const [searchValue] = useDebounce(internalValue, 1000);
    const searchValueRef = React.useRef(searchValue);
    const [internalSelectedList, setInternalSelectedList] = React.useState<
      string[]
    >([]);
    const [customValues, setCustomValues] = React.useState<string[]>([]);

    const datalistRef = React.useRef<HTMLDataListElement | null>(null);
    const chipsWrapperRef = React.useRef<HTMLDivElement | null>(null);
    const trailingIconRef = React.useRef<HTMLButtonElement | null>(null);
    const mainWrapperRef = useClickAway(() => {
      if (datalistRef.current && trailingIconRef.current) {
        if (datalistRef.current.style && trailingIconRef.current?.style) {
          datalistRef.current.style.display = 'none';
          trailingIconRef.current.style.transform = 'rotate(0deg)';
        }
      }

      if (ref.current) {
        if (
          ref.current.value === '' &&
          internalSelectedList.length === 0 &&
          as !== 'select'
        ) {
          setClicked(false);
        }

        if (
          ((ref.current.value === '' && variant !== 'multi') ||
            (internalSelectedList.length === 0 && variant === 'multi')) &&
          as === 'select'
        ) {
          setClicked(false);
        }
      }
    });
    const iconColorRegistry = {
      default: 'var(--color_neutral-dark)',
      error: 'var(--color_error-dark)',
      warning: 'var(--color_warning)',
      disabled: 'var(--color_disabled)',
    };

    const { setNativeValue } = useNativeInput(name ?? '', 'text');

    const modeOf = React.useCallback(
      (text: string): DropdownInputMode =>
        as === 'search' && variant === 'simple' && getInputMode
          ? getInputMode(text)
          : 'search',
      [as, variant, getInputMode],
    );
    const inputMode = modeOf(edited ? internalValue : value ?? '');
    const fallbackId = React.useId();
    const stateTextId = `${props.id || name || fallbackId}-state`;

    /** Saves free text as typed, with no lookup key; true when it saved. */
    const saveFreeText = () => {
      const typed = (ref.current?.value ?? '').trim();
      if (modeOf(typed) !== 'free' || typed === (value ?? '')) return false;
      setNativeValue(typed);
      return true;
    };

    //#region Effects

    React.useEffect(() => {
      // Free and blocked text never reaches the lookup.
      if (
        searchValueRef.current !== searchValue &&
        as === 'search' &&
        modeOf(searchValue) === 'search'
      ) {
        setNativeValue(`${config.searchPrefix}${searchValue}`);
        searchValueRef.current = searchValue;
      }
    }, [searchValue, name, as, setNativeValue, modeOf]);

    React.useLayoutEffect(() => {
      setEdited(false);
      if (variant === 'simple') {
        setClicked(variant === 'simple' && value !== undefined && value !== '');
        setShowClearIcon(
          variant === 'simple' && value !== undefined && value !== '',
        );
      }

      if (ref.current) {
        ref.current.value = variant === 'simple' ? value?.toString() ?? '' : '';
      }

      if (hiddenInputRef.current) {
        if (variant === 'multi') {
          if (!value) {
            hiddenInputRef.current.value = '';
          } else {
            const selectedOptions: string[] = value?.split(',');
            const jointOptions = selectedOptions.join(config.multiValueDivider);
            hiddenInputRef.current.value = jointOptions;

            const splittedOptions = jointOptions.split(
              config.multiValueDivider,
            );
            if (splittedOptions.length > 0) {
              const filteredCustomValues = splittedOptions.filter(
                item =>
                  !item.startsWith(config.externalLookupPrefix) &&
                  !item.startsWith(config.internalLookupPrefix),
              );
              setCustomValues(filteredCustomValues);
            }
          }
        } else {
          hiddenInputRef.current.value = value ?? '';
        }
      }
    }, [value, variant]);

    React.useLayoutEffect(() => {
      setInternalList(datalist.options);
      if (as === 'search') {
        setShowNoResultsOption(datalist.options.length === 0);
      }
    }, [datalist.options, as]);

    React.useLayoutEffect(() => {
      const selectedItems = internalList?.filter(item => item.selected);
      const selectedOptions: string[] =
        selectedItems?.map(item => item.key) ?? [];

      if (hiddenInputRef.current && selectedOptions.length > 0) {
        hiddenInputRef.current.value = selectedOptions.join(
          config.multiValueDivider,
        );
      }

      const newSelectedList = selectedItems?.map(item => item.value) ?? [];

      setInternalSelectedList([...newSelectedList, ...customValues]);
    }, [internalList, customValues]);

    React.useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        if (ref.current && chipsWrapperRef.current) {
          const chipsWrapperHeight = chipsWrapperRef.current?.offsetHeight ?? 0;
          ref.current.style.height =
            chipsWrapperHeight > 0
              ? `calc(${chipsWrapperHeight}px + 48px)`
              : '48px';
          ref.current.style.paddingBottom =
            chipsWrapperHeight > 0 ? `calc(${chipsWrapperHeight}px + 9px)` : '';
        }
      });

      const element = document.getElementById(`${name}_chip`);

      if (element) {
        resizeObserver.observe(element);
      }

      if (internalSelectedList.length > 0) {
        setClicked(true);
      } else {
        if (ref.current) {
          ref.current.style.height = '48px';
          ref.current.style.paddingBottom = '';
        }
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [internalSelectedList.length, name]);

    const reportedModeRef = React.useRef<{
      mode: DropdownInputMode;
      live: boolean;
    } | null>(null);
    React.useEffect(() => {
      const reported = reportedModeRef.current;
      if (reported?.mode === inputMode && reported.live === edited) return;
      reportedModeRef.current = { mode: inputMode, live: edited };
      onInputModeChange?.(inputMode, edited);
    }, [inputMode, edited, onInputModeChange]);

    const listedModeRef = React.useRef(inputMode);
    React.useLayoutEffect(() => {
      if (listedModeRef.current === inputMode) return;
      listedModeRef.current = inputMode;
      const list = datalistRef.current;
      const chevron = trailingIconRef.current;
      if (!list || !chevron) return;

      // Only search mode lists suggestions; back in it, a focused field shows them again.
      if (inputMode !== 'search') {
        list.style.display = 'none';
        chevron.style.transform = 'rotate(0deg)';
      } else if (document.activeElement === ref.current) {
        list.style.display = 'flex';
        list.style.flexDirection = 'column';
        chevron.style.transform = 'rotate(180deg)';
      }
    }, [inputMode]);

    // The caller swaps its state text a render after hearing of a mode change,
    // so the announcement waits for that text instead of reading it with the mode.
    const currentModeRef = React.useRef(inputMode);
    const announcedModeRef = React.useRef(inputMode);
    React.useLayoutEffect(() => {
      currentModeRef.current = inputMode;
    });
    React.useEffect(() => {
      if (announcedModeRef.current === currentModeRef.current) return;
      announcedModeRef.current = currentModeRef.current;
      setModeAnnouncement(stateText ?? '');
    }, [stateText]);

    //#endregion

    const noResultsText =
      as === 'search' && internalValue.trim() === ''
        ? 'Start typing to search.'
        : 'No results found.';

    return (
      <div
        className={clsx('dropdown-wrapper', `dropdown-${state}`, {
          'dropdown-disabled': disabled,
          'dropdown-with-hint': hint,
        })}
      >
        <div className="dd-main-wrapper" ref={mainWrapperRef}>
          {leadingIcon && <span className="leading-icon">{leadingIcon}</span>}
          <div>
            <label
              className={`label-${state} ${clicked ? `clicked-${state}` : ''}`}
              htmlFor={props.id || name}
            >
              {leadingIcon && (
                <span
                  className={`${
                    clicked
                      ? 'label-leading-icon_clicked'
                      : 'label-leading-icon'
                  }`}
                >
                  {leadingIcon}
                </span>
              )}{' '}
              {label}{' '}
              {required && (
                <span style={{ color: 'var(--color_error)' }}>*</span>
              )}
            </label>
            {internalSelectedList.length > 0 &&
              as === 'select' &&
              variant === 'multi' && (
                <div
                  className="chips-wrapper"
                  id={`${name}_chip`}
                  ref={chipsWrapperRef}
                >
                  <ChipGroup
                    kind={disabled === true ? 'disabled' : 'neutral'}
                    disabled={disabled}
                    onClick={chip => {
                      const removedOption = internalList.find(
                        o => o.value === chip,
                      );

                      const removedCustomValue = customValues.find(
                        v => v === chip,
                      );

                      if (hiddenInputRef.current && ref.current) {
                        const selectedOptions: string[] =
                          hiddenInputRef.current.value
                            .split(config.multiValueDivider)
                            .filter(v => v !== '');

                        if (removedOption) {
                          selectedOptions.splice(
                            selectedOptions.indexOf(removedOption.key),
                            1,
                          );
                        }

                        let selectedCustomValues = customValues.filter(
                          v => v !== '',
                        );

                        if (removedCustomValue) {
                          selectedCustomValues = selectedCustomValues.filter(
                            v => v !== removedCustomValue,
                          );

                          setCustomValues(selectedCustomValues);
                        }

                        const jointSelectedOptions = selectedOptions.join(
                          config.multiValueDivider,
                        );

                        const jointCustomValues = selectedCustomValues.join(
                          config.multiValueDivider,
                        );

                        const jointOptions = [
                          jointSelectedOptions,
                          jointCustomValues,
                        ]
                          .filter(v => v !== '')
                          .join(config.multiValueDivider);

                        if (jointOptions === '') {
                          setClicked(false);
                        }

                        // This needs to be done to clear the input
                        setNativeValue('-');
                        setNativeValue(jointOptions);

                        ref.current.value = '';
                      }

                      onRemove?.(chip);
                    }}
                    chips={internalSelectedList}
                  />
                </div>
              )}

            <input
              title={value}
              className={`${leadingIcon ? 'with-leading-icon' : ''}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setInternalValue(e.target.value);
                setEdited(true);
                if (e.target.value !== '') {
                  setClicked(true);
                  setShowClearIcon(true);
                } else {
                  if (internalSelectedList.length === 0) {
                    setClicked(false);
                    setShowClearIcon(false);
                  }

                  if (
                    e.target.value === '' &&
                    clicked &&
                    modeOf('') === 'search'
                  ) {
                    if (datalistRef.current?.style) {
                      datalistRef.current.style.display = 'flex';
                      datalistRef.current.style.flexDirection = 'column';
                    }
                  }
                }

                if (as === 'select' && datalistRef.current) {
                  let hiddenOptionsCounter = 0;

                  for (const wrapper of datalistRef.current.children) {
                    if (ref.current) {
                      const testValue = (
                        wrapper.children[
                          datalist.iconPosition === 'leading' ? 1 : 0
                        ] as HTMLOptionElement
                      ).value.toUpperCase();

                      if (
                        testValue === '-1' ||
                        testValue.startsWith(config.customValuePrefix) ||
                        testValue.indexOf(ref.current.value.toUpperCase()) > -1
                      ) {
                        (wrapper as HTMLButtonElement).style.display = 'flex';
                      } else {
                        (wrapper as HTMLButtonElement).style.display = 'none';
                        hiddenOptionsCounter++;
                      }
                    }
                  }

                  setShowNoResultsOption(
                    hiddenOptionsCounter >= internalList.length,
                  );
                }
              }}
              onFocus={() => {
                onFocusProp?.();
                setClicked(true);
                if (
                  inputMode === 'search' &&
                  datalistRef.current &&
                  trailingIconRef.current
                ) {
                  if (
                    datalistRef.current.style &&
                    trailingIconRef.current.style
                  ) {
                    datalistRef.current.style.display = 'flex';
                    datalistRef.current.style.flexDirection = 'column';
                    trailingIconRef.current.style.transform = 'rotate(180deg)';
                  }
                }
              }}
              type={type}
              disabled={disabled}
              ref={ref}
              list=""
              autoComplete="off"
              aria-describedby={stateText ? stateTextId : undefined}
              onKeyDown={e => {
                // Enter keeps free text instead of submitting the form.
                if (
                  e.key === 'Enter' &&
                  modeOf(e.currentTarget.value) === 'free'
                ) {
                  e.preventDefault();
                  saveFreeText();
                }
                onKeyDown?.(e);
              }}
              onBlur={e => {
                // Before the early return, so Tab to the clear or chevron button still saves.
                const savedFreeText = saveFreeText();
                const relatedTarget = e.relatedTarget as Node | null;
                if (
                  relatedTarget &&
                  mainWrapperRef.current?.contains(relatedTarget)
                ) {
                  return;
                }

                if (
                  !savedFreeText &&
                  as === 'search' &&
                  variant === 'simple' &&
                  ref.current
                ) {
                  const typedValue = ref.current.value;
                  const committedLabel = value ?? '';
                  const action = resolveTypeaheadBlurAction(
                    typedValue,
                    committedLabel,
                  );

                  if (action === 'clear') {
                    setNativeValue('');
                    setInternalValue('');
                    ref.current.value = '';
                    setShowClearIcon(false);
                  } else if (action === 'revert') {
                    ref.current.value = committedLabel;
                    setInternalValue(committedLabel);
                    setShowClearIcon(committedLabel !== '');
                    setClicked(committedLabel !== '');
                    setEdited(false);
                    onInvalidEdit?.(inputMode);
                  }
                }

                if (
                  hiddenInputRef.current &&
                  internalSelectedList.length === 0
                ) {
                  const hidden = hiddenInputRef.current;
                  if (savedFreeText) {
                    // A tick later the form holds the saved text. The blur event is
                    // dispatched, not focus moved, so focus stays where the user sent it.
                    setTimeout(() =>
                      hidden.dispatchEvent(
                        new FocusEvent('focusout', { bubbles: true }),
                      ),
                    );
                  } else {
                    hidden.focus();
                    hidden.blur();
                  }
                }
              }}
              {...props}
            />
            <input
              style={{
                opacity: 0,
                width: 0,
                height: 0,
                overflow: 'hidden',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
              aria-hidden
              type="text"
              onChange={onChange}
              name={name}
              onBlur={onBlur}
              tabIndex={-1}
              ref={hiddenInputRef}
            />
            {includeClearIcon && showClearIcon && variant === 'simple' && (
              <button
                type="button"
                className="fix-icon-erase"
                onClick={() => {
                  if (ref.current) {
                    setNativeValue('');

                    setInternalValue('');
                    setEdited(true);
                    ref.current.value = '';

                    if (as === 'search') {
                      ref.current.focus();
                    }
                  }

                  setShowClearIcon(false);
                  setShowNoResultsOption(false);

                  if (datalistRef.current) {
                    if (datalistRef.current.style && modeOf('') === 'search') {
                      datalistRef.current.style.display = 'flex';
                      datalistRef.current.style.flexDirection = 'column';
                    }

                    for (const wrapper of datalistRef.current.children) {
                      (wrapper as HTMLButtonElement).style.display = 'flex';
                    }
                  }
                }}
              >
                <Icon type="x" size="small" />
              </button>
            )}
            {stateText && (
              <div className="dd-bottom-wrapper">
                <div className="left">
                  {state === 'default' && stateDefaultIcon ? (
                    stateDefaultIcon
                  ) : (
                    <Icon
                      type={
                        state === 'error'
                          ? 'x'
                          : state === 'warning'
                          ? 'warning'
                          : null
                      }
                      color={
                        disabled === true
                          ? iconColorRegistry.disabled
                          : iconColorRegistry[state]
                      }
                    />
                  )}
                  {state && (
                    <div className="state-text" id={stateTextId}>
                      {stateText}
                    </div>
                  )}
                </div>
              </div>
            )}
            <datalist
              ref={datalistRef}
              className={showNoResultsOption ? 'datalist-not-found' : ''}
              id={`${props.id}-list`}
              role="listbox"
            >
              {showNoResultsOption && (
                <button
                  type="button"
                  className="option-wrapper option-wrapper-not-found"
                  style={{
                    justifyContent:
                      datalist.iconPosition === 'leading'
                        ? 'start'
                        : 'space-between',
                  }}
                >
                  <option value={-1}>{noResultsText}</option>
                </button>
              )}
              {canAddCustom && internalValue && (
                <button
                  type="button"
                  className="option-wrapper"
                  style={{
                    justifyContent:
                      datalist.iconPosition === 'leading'
                        ? 'start'
                        : 'space-between',
                  }}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => {
                    if (variant === 'multi') {
                      const newCustomValues = [...customValues, internalValue];

                      const jointCustomValues = newCustomValues.join(
                        config.multiValueDivider,
                      );

                      const newInternalValue = [
                        hiddenInputRef.current?.value,
                        jointCustomValues,
                      ]
                        .filter(v => v !== '')
                        .join(config.multiValueDivider);

                      setNativeValue(newInternalValue);
                      setCustomValues(newCustomValues);
                    } else {
                      setNativeValue(internalValue);
                    }

                    setInternalValue('');
                    setShowNoResultsOption(false);
                    if (datalistRef.current) {
                      for (const wrapper of datalistRef.current.children) {
                        (wrapper as HTMLButtonElement).style.display = 'flex';
                      }
                    }
                  }}
                >
                  <option
                    value={`${config.customValuePrefix}${internalValue}`}
                    className="option-custom-use"
                  >
                    {`Use: ${internalValue}`}
                  </option>
                </button>
              )}
              {internalList?.map((option, idx) => {
                return (
                  <button
                    key={`${idx}_${option.value}`}
                    type="button"
                    className={`option-wrapper ${
                      option.selected ? 'option-wrapper-selected' : ''
                    } ${option.disabled ? 'option-wrapper-disabled' : ''}`}
                    style={{
                      justifyContent:
                        datalist.iconPosition === 'leading'
                          ? 'start'
                          : 'space-between',
                    }}
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => {
                      if (option.disabled) {
                        return;
                      }

                      if (datalistRef.current) {
                        for (const wrapper of datalistRef.current.children) {
                          (wrapper as HTMLButtonElement).style.display = 'flex';
                        }
                      }

                      if (variant === 'simple') {
                        if (internalSelectedList.length < 1) {
                          onToggle(e);
                        }
                      } else {
                        onToggle(e);
                      }

                      setClicked(true);
                      setShowClearIcon(true);

                      if (datalistRef.current && variant === 'simple') {
                        if (datalistRef.current.style) {
                          datalistRef.current.style.display = 'none';
                        }
                      }

                      if (as !== 'select') {
                        if (ref.current) {
                          setNativeValue(option.key);
                          ref.current.value = option.value;
                        }
                      } else {
                        if (ref.current && hiddenInputRef.current) {
                          let selectedOptions: string[] = [];

                          selectedOptions = hiddenInputRef.current.value
                            .split(config.multiValueDivider)
                            .filter(v => v !== '')
                            .filter(v => v !== config.searchPrefix);

                          if (selectedOptions.includes(option.key)) {
                            selectedOptions.splice(
                              selectedOptions.indexOf(option.key),
                              1,
                            );
                          } else {
                            if (variant === 'multi') {
                              selectedOptions.push(option.key);
                            } else {
                              selectedOptions = [option.key];
                            }
                          }

                          if (variant === 'multi') {
                            selectedOptions.push(...customValues);
                          }

                          setNativeValue(
                            selectedOptions.join(config.multiValueDivider),
                          );

                          ref.current.value =
                            variant === 'multi' ? '' : option.value;
                        }
                      }
                    }}
                  >
                    {datalist.iconPosition === 'leading' && (
                      <div className="icon-leading">
                        {option.icon ? option.icon : datalist.defaultIcon}
                      </div>
                    )}
                    <option value={option.value}>{option.value}</option>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                        alignItems: 'center',
                      }}
                    >
                      {option.selected && (
                        <div
                          style={{
                            height: '18px',
                            width: '18px',
                          }}
                        >
                          <Icon
                            type="success"
                            size="medium"
                            color="var(--color_primary-darkest)"
                          />
                        </div>
                      )}
                      {datalist.iconPosition === 'trailing' && (
                        <div
                          className="icon-trailing"
                          style={{ marginTop: '-1px' }}
                        >
                          {option.icon ? option.icon : datalist.defaultIcon}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </datalist>
          </div>
          <button
            type="button"
            className="trailing-icon"
            ref={trailingIconRef}
            onClick={() => {
              // Only search mode lists suggestions.
              if (
                !disabled &&
                inputMode === 'search' &&
                trailingIconRef.current &&
                datalistRef.current
              ) {
                if (
                  ['', 'rotate(0deg)'].includes(
                    trailingIconRef.current.style.transform,
                  )
                ) {
                  trailingIconRef.current.style.transform = 'rotate(180deg)';
                  datalistRef.current.style.display = 'flex';
                  datalistRef.current.style.flexDirection = 'column';
                } else {
                  trailingIconRef.current.style.transform = 'rotate(0deg)';
                  datalistRef.current.style.display = 'none';
                }
              }
            }}
          >
            <Icon
              type="arrow-down"
              size="large"
              color={
                disabled ? 'var(--color_disabled)' : 'var(--color_primary)'
              }
            />
          </button>
        </div>
        {/* Follows the field so it paints over the border; a z-index would also lift it over the sticky mobile header. */}
        {hint && (
          <div className="dd-top-wrapper">
            <Hint title={label} {...hint} />
          </div>
        )}
        <span className="dd-live-region" aria-live="polite">
          {modeAnnouncement}
        </span>
      </div>
    );
  },
);

export { Dropdown };
